import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { formatPrice } from "@/utils/currency";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  Users, Phone, Mail, ShoppingBag, Star, TrendingUp, DollarSign,
  Calendar, Loader2, Gift, Clock, X, Save, Edit2,
} from "lucide-react";
import { SOURCE_LABELS, SOURCE_COLORS, STATUS_LABELS, STATUS_COLORS, getInitials, formatDateShort, formatDateTime } from "./customersUtils";
import { SendCouponDialog } from "./SendCouponDialog";

export function CustomerDetail({ phone, open, onClose }) {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios); authAxiosRef.current = authAxios;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [couponOpen, setCouponOpen] = useState(false);

  useEffect(() => {
    if (!phone || !open) return;
    setLoading(true);
    authAxiosRef.current.get(`/customers/${encodeURIComponent(phone)}`)
      .then(r => {
        setData(r.data);
        setEditForm({
          name: r.data.customer?.name || "",
          notes: r.data.customer?.notes || "",
          opt_out_mass_messages: r.data.customer?.opt_out_mass_messages || false,
        });
      })
      .catch(() => toast.error("Error cargando detalle"))
      .finally(() => setLoading(false));
  }, [phone, open]);

  const saveEdit = async () => {
    setSaving(true);
    try {
      await authAxios.patch(`/customers/${encodeURIComponent(phone)}`, editForm);
      toast.success("Cliente actualizado");
      setData(prev => ({ ...prev, customer: { ...prev.customer, ...editForm } }));
      setEditing(false);
    } catch { toast.error("Error al guardar"); }
    setSaving(false);
  };

  const cust = data?.customer;
  const orders = data?.orders || [];

  return (
    <Sheet open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-xl p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <SheetTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-primary" /> Detalle del Cliente</SheetTitle>
        </SheetHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : cust ? (
          <ScrollArea className="flex-1">
            <div className="p-6 space-y-6">
              {/* Header */}
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center text-lg font-bold text-primary flex-shrink-0">{getInitials(cust.name)}</div>
                <div className="flex-1 min-w-0">
                  {editing ? (
                    <Input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className="mb-2 font-semibold" data-testid="edit-customer-name" />
                  ) : (
                    <h2 className="text-xl font-bold truncate" data-testid="customer-detail-name">{cust.name || "Sin nombre"}</h2>
                  )}
                  <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mt-1">
                    <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {cust.phone}</span>
                    {cust.email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {cust.email}</span>}
                  </div>
                  {cust.sources?.length > 0 && (
                    <div className="flex gap-1.5 mt-2 flex-wrap">
                      {cust.sources.map(s => (<Badge key={s} variant="secondary" className={`text-[10px] ${SOURCE_COLORS[s] || ""}`}>{SOURCE_LABELS[s] || s}</Badge>))}
                    </div>
                  )}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {editing ? (
                    <>
                      <Button size="sm" onClick={saveEdit} disabled={saving} data-testid="save-customer-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}</Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(false)}><X className="w-4 h-4" /></Button>
                    </>
                  ) : (
                    <>
                      <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="edit-customer-btn"><Edit2 className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline" onClick={() => setCouponOpen(true)} data-testid="send-coupon-btn"><Gift className="w-4 h-4" /></Button>
                    </>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: "Pedidos",      value: cust.total_orders,              icon: ShoppingBag, color: "text-blue-600"   },
                  { label: "Total",        value: formatPrice(cust.total_spent),  icon: DollarSign,  color: "text-teal-600"   },
                  { label: "Ticket Medio", value: formatPrice(cust.avg_ticket || 0), icon: TrendingUp, color: "text-amber-600" },
                  { label: "Puntos",       value: cust.points,                    icon: Star,        color: "text-purple-600" },
                ].map(s => (
                  <div key={s.label} className="bg-muted/40 rounded-lg p-3 text-center">
                    <s.icon className={`w-4 h-4 ${s.color} mx-auto mb-1`} />
                    <p className="text-lg font-bold leading-none">{s.value}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">{s.label}</p>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Desde: {formatDateShort(cust.first_order_at)}</span>
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Último: {formatDateShort(cust.last_order_at)}</span>
              </div>

              {editing && (
                <div className="space-y-3 bg-muted/30 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Desactivar mensajes masivos</Label>
                    <Switch checked={editForm.opt_out_mass_messages} onCheckedChange={v => setEditForm({ ...editForm, opt_out_mass_messages: v })} data-testid="opt-out-toggle" />
                  </div>
                  <div>
                    <Label className="text-sm mb-1 block">Notas</Label>
                    <Textarea value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} placeholder="Notas internas sobre este cliente..." rows={3} data-testid="customer-notes" />
                  </div>
                </div>
              )}

              {!editing && cust.notes && (
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Notas</p>
                  <p className="text-sm">{cust.notes}</p>
                </div>
              )}

              {cust.opt_out_mass_messages && !editing && (
                <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">Mensajes masivos desactivados</Badge>
              )}

              <Separator />

              {/* Order History */}
              <div>
                <h3 className="text-base font-bold mb-3 flex items-center gap-2"><ShoppingBag className="w-4 h-4" /> Historial de Pedidos ({orders.length})</h3>
                {orders.length > 0 ? (
                  <div className="space-y-2">
                    {orders.map((o, i) => (
                      <div key={o.id || i} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors" data-testid={`order-item-${i}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="text-center flex-shrink-0 w-10">
                            <p className="text-xs text-muted-foreground">{o.order_number ? `#${o.order_number}` : `#${i + 1}`}</p>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">
                              {(o.items || []).slice(0, 2).map(it => it.product_name).join(", ")}
                              {(o.items || []).length > 2 && ` +${o.items.length - 2}`}
                            </p>
                            <p className="text-xs text-muted-foreground">{formatDateTime(o.created_at)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <Badge variant="secondary" className={`text-[10px] ${STATUS_COLORS[o.status] || ""}`}>{STATUS_LABELS[o.status] || o.status}</Badge>
                          <span className="text-sm font-bold">{formatPrice(o.total)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-6">Sin pedidos registrados</p>
                )}
              </div>

              {(data?.loyalty_events || []).length > 0 && (
                <>
                  <Separator />
                  <div>
                    <h3 className="text-base font-bold mb-3 flex items-center gap-2"><Star className="w-4 h-4" /> Historial de Puntos</h3>
                    <div className="space-y-1">
                      {data.loyalty_events.map((ev, i) => (
                        <div key={i} className="flex items-center justify-between py-2 text-sm">
                          <div>
                            <p className="font-medium">{ev.type === "earn" ? "Ganó puntos" : ev.type === "redeem" ? "Canjeó puntos" : ev.type}</p>
                            <p className="text-xs text-muted-foreground">{formatDateShort(ev.created_at)}</p>
                          </div>
                          <span className={`font-bold ${ev.points > 0 ? "text-emerald-600" : "text-red-500"}`}>{ev.points > 0 ? "+" : ""}{ev.points}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </ScrollArea>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">No se encontró el cliente</div>
        )}

        <SendCouponDialog phone={phone} open={couponOpen} onClose={() => setCouponOpen(false)} customerName={cust?.name || ""} />
      </SheetContent>
    </Sheet>
  );
}
